import { useState, useEffect, useCallback } from 'react';
import addonAssignmentService, { type AddonSeatSummary } from '../services/addonAssignmentService';
import { useUserContext } from './useUserContext';
import { getApiErrorInfo } from '../services/api';

export interface AdminSelfAssignResult {
  seatAvailable: boolean | null;
  activating: boolean;
  activated: boolean;
  error: string | null;
  activateSeat: () => Promise<void>;
}

export function useAdminSelfAssign(
  addonId: string | undefined,
  enabled: boolean,
): AdminSelfAssignResult {
  const { backendProfile } = useUserContext();
  const [seatAvailable, setSeatAvailable] = useState<boolean | null>(null);
  const [activating, setActivating] = useState(false);
  const [activated, setActivated] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tenantId = backendProfile?.tenantId;
  const userId = backendProfile?.id;

  useEffect(() => {
    if (!enabled || !addonId || !tenantId) {
      setSeatAvailable(null);
      return;
    }

    addonAssignmentService.getSeatSummary(tenantId)
      .then((summaries: AddonSeatSummary[]) => {
        const entry = summaries.find(s => s.addonId === addonId);
        setSeatAvailable(entry ? entry.availableCount > 0 : false);
      })
      .catch(() => setSeatAvailable(false));
  }, [enabled, addonId, tenantId]);

  const activateSeat = useCallback(async () => {
    if (!tenantId || !userId || !addonId) return;
    setActivating(true);
    setError(null);
    try {
      await addonAssignmentService.assignSeat(tenantId, userId, addonId);
      setActivated(true);
    } catch (err: unknown) {
      const apiError = getApiErrorInfo(err);
      setError(apiError.message || 'Failed to activate access. Please try again.');
    } finally {
      setActivating(false);
    }
  }, [tenantId, userId, addonId]);

  return { seatAvailable, activating, activated, error, activateSeat };
}
